import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Grid,
  Card,
  CardContent,
  Avatar,
  Link,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import MigrationModal from "../MigrationModal/MigrationModal";
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  LockOpen as LockOpenIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_ENTITY_STATUS } from "../../../graphql/mutations";
import { DELETE_ENTITY } from "../../../graphql/Admins/mutation";
import {
  GET_MANAGEMENT_ACTIVITIES,
  GET_ORGANIZATION_BY_ID,
  GET_INDIVIDUAL_BY_ID,
} from "../../../graphql/queries";
import { Organization, Individual } from "../../../../Types";
import toast from "react-hot-toast";
import moment from "moment";
import { socialLinksHandler } from "../../../helpers/MasjidExternalLinkHandler";
import { getValidUrl } from "../../PageComponents/MasjidTable/Helper";
import ReasonConfirmation from "../ReasonConfirmation/ReasonConfirmation";
import DeleteConfirmation from "../../AdminComponents/ExternalAdmins/DeleteConfirmation";
import ManagementLogs from "../ManagementLogs/ManagementLogs";
import { adminFromLocalStg } from "../../../helpers/AdminFromLocalStorage/AdminFromLocalStorage";

const ValueFieldStyles = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: "#1B8368",
  fontWeight: "bold",
};

const getValueFieldStyles = (value: any) => {
  if (value) {
    return ValueFieldStyles;
  } else {
    return { ...ValueFieldStyles, color: "#FF4949" };
  }
};

interface StatusHandlerModalProps {
  open: boolean;
  onClose: () => void;
  entity: Organization | Individual | null;
  entityType: "ORGANIZATION" | "INDIVIDUAL";
  onStatusUpdate: () => void;
}

const StatusHandlerModal: React.FC<StatusHandlerModalProps> = ({
  open,
  onClose,
  entity,
  entityType,
  onStatusUpdate,
}) => {
  const [message, setMessage] = useState("");
  const [selectedAction, setSelectedAction] = useState<
    "approve" | "reject" | "freeze" | "unfreeze" | "delete" | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docsVerified, setDocsVerified] = useState(false);
  const [refreshedEntity, setRefreshedEntity] = useState<
    Organization | Individual | null
  >(null);
  const [showLogs, setShowLogs] = useState(false);
  const [showReasonConfirmation, setShowReasonConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [loggedInUserInfo] = useState(adminFromLocalStg());

  // Fetch management activities
  const {
    data: activitiesData,
    loading: activitiesLoading,
    refetch: refetchActivities,
  } = useQuery(GET_MANAGEMENT_ACTIVITIES, {
    variables: {
      entityId: entity?._id || "",
      entityType: entityType,
    },
    skip: !entity?._id || !open,
    fetchPolicy: "network-only",
  });

  // Fetch updated organization data
  const { data: orgData, refetch: refetchOrg } = useQuery(
    GET_ORGANIZATION_BY_ID,
    {
      variables: { id: entity?._id || "" },
      skip: !entity?._id || entityType !== "ORGANIZATION" || !open,
      fetchPolicy: "network-only",
    }
  );

  // Fetch updated individual data
  const { data: individualData, refetch: refetchIndividual } = useQuery(
    GET_INDIVIDUAL_BY_ID,
    {
      variables: { id: entity?._id || "" },
      skip: !entity?._id || entityType !== "INDIVIDUAL" || !open,
      fetchPolicy: "network-only",
    }
  );

  // Use refreshed entity data if available, otherwise use original entity
  const currentEntity = refreshedEntity || entity;

  // Update entity status mutation
  const [updateEntityStatus] = useMutation(UPDATE_ENTITY_STATUS, {
    onCompleted: async (data) => {
      toast.success("Status updated successfully!");
      setIsSubmitting(false);

      // Refresh entity data and activities
      try {
        await Promise.all([
          refetchActivities(),
          entityType === "ORGANIZATION" ? refetchOrg() : refetchIndividual(),
        ]);
      } catch (error) {
        console.error("Error refreshing data:", error);
      }
      setShowReasonConfirmation(false);
      // Keep modal open and refresh parent data
      onStatusUpdate();
      // Reset form
      setMessage("");
      setSelectedAction(null);
      setDocsVerified(false);
    },
    onError: (error) => {
      console.error("Error updating status:", error);
      toast.error("Failed to update status. Please try again.");
      setIsSubmitting(false);
    },
    refetchQueries: [
      "GetOrganizations",
      "GetIndividuals",
      "organizationTotals",
      "individualTotals",
      "ManagementActivities",
    ],
  });

  // Delete entity mutation
  const [deleteEntity] = useMutation(DELETE_ENTITY, {
    onCompleted: () => {
      toast.success("Entity deleted successfully and moved to archives");
      setDeleteLoading(false);
      setShowDeleteConfirmation(false);
      onStatusUpdate();
      onClose();
    },
    onError: (error) => {
      console.error("Delete entity error:", error);
      toast.error("Failed to delete entity. Please try again.");
      setDeleteLoading(false);
    },
  });

  // Handle delete entity
  const handleDeleteEntity = async (reason: string, description: string) => {
    if (!currentEntity) return;

    setDeleteLoading(true);
    try {
      await deleteEntity({
        variables: {
          input: {
            entityId: currentEntity._id,
            entityType: entityType,
            reason: reason,
            description: description,
            metadata: JSON.stringify({
              entityName:
                entityType === "ORGANIZATION"
                  ? (currentEntity as Organization).name
                  : (currentEntity as Individual).name,
              deletedFrom: `${entityType}Panel`,
            }),
          },
        },
      });
    } catch (error) {
      // Error is handled by onError callback
    }
  };

  useEffect(() => {
    if (open) {
      setMessage("");
      setSelectedAction(null);
      setDocsVerified(false);
      setRefreshedEntity(null);
      setShowLogs(false);
      setShowReasonConfirmation(false);
      setShowDeleteConfirmation(false);
      setDeleteLoading(false);
      setLoading(false);
      setMessageError("");
      setReasonError(false);
      setShowMigrationModal(false);
    }
  }, [open]);

  // Update refreshed entity when query data changes
  useEffect(() => {
    if (entityType === "ORGANIZATION" && orgData?.getOrganizationById) {
      setRefreshedEntity(orgData.getOrganizationById);
    } else if (
      entityType === "INDIVIDUAL" &&
      individualData?.getIndividualById
    ) {
      setRefreshedEntity(individualData.getIndividualById);
    }
  }, [orgData, individualData, entityType]);

  const handleSubmit = () => {
    if (!selectedAction || !currentEntity) return;

    if (!message.trim()) {
      setReasonError(true);
      return;
    }

    setIsSubmitting(true);

    let status = "";
    let freeze = false;

    if (selectedAction === "approve") {
      status = "approved";
      freeze = false;
    } else if (selectedAction === "reject") {
      status = "frozen";
      freeze = true;
    } else if (selectedAction === "unfreeze") {
      status = "approved";
      freeze = false;
    }

    updateEntityStatus({
      variables: {
        input: {
          entityId: currentEntity._id,
          entityType: entityType,
          status: status,
          message: message || undefined,
        },
        freeze: freeze,
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "#00D23B";
      case "pending":
        return "#EDA920";
      case "rejected":
      case "frozen":
        return "#FF4949";
      default:
        return "#1B8368";
    }
  };

  const getAvailableActions = () => {
    const actions = [];

    if (currentEntity?.isFreezed) {
      actions.push({
        action: "unfreeze",
        label: "Unfreeze",
        icon: <LockOpenIcon />,
        color: "#4caf50",
      });
    } else {
      if (!currentEntity?.isVerified) {
        actions.push({
          action: "approve",
          label: "Approve",
          icon: <CheckCircleIcon />,
          color: "#4caf50",
        });
      }
      actions.push({
        action: "reject",
        label: "Reject/Freeze",
        icon: <BlockIcon />,
        color: "#f44336",
      });
    }

    return actions;
  };

  const getDeleteAction = () => {
    return {
      action: "delete",
      label: "Delete",
      icon: <DeleteIcon />,
      color: "#FF2D30",
    };
  };

  const getAllActions = () => {
    const regularActions = getAvailableActions();
    const deleteAction = getDeleteAction();
    return [...regularActions, deleteAction];
  };

  const getEmailFromExternalLinks = () => {
    // Since externalLinks is not in the current queries, return null
    return null;
  };

  const activities = activitiesData?.managementActivities || [];

  function isOrganization(entity: any): entity is Organization {
    return (
      entity &&
      typeof entity === "object" &&
      "organizationType" in entity &&
      Array.isArray(entity.organizationLinks)
    );
  }

  const approveDisabled =
    selectedAction === "approve" &&
    entityType === "ORGANIZATION" &&
    isOrganization(currentEntity) &&
    (currentEntity?.organizationLinks?.length || 0) > 0 &&
    !docsVerified;

  const handleAction = (action: string) => {
    if (action === "delete") {
      setShowDeleteConfirmation(true);
    } else {
      setSelectedAction(action as any);
      setShowReasonConfirmation(true);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const isLoading = isSubmitting || loading;

  // Define field structure for the entity
  const getEntityFields = () => {
    if (!currentEntity) return [];

    const fields = [
      {
        label: "Type",
        value:
          entityType === "ORGANIZATION"
            ? (currentEntity as Organization)?.organizationType
            : (currentEntity as Individual)?.individualType,
        showCopy: true,
      },
      {
        label: "Full Name",
        value: currentEntity.name,
        showCopy: true,
      },
      {
        label: "Email Address",
        value: getEmailFromExternalLinks(),
        showCopy: true,
      },
      {
        label: "Contact Number",
        value: currentEntity.contact,
        showCopy: true,
      },
      {
        label: "Website",
        value: socialLinksHandler("Website", currentEntity.externalLinks || []),
        showCopy: true,
        isLink: true,
        linkHref: socialLinksHandler(
          "Website",
          currentEntity.externalLinks || []
        )
          ? getValidUrl(
              socialLinksHandler("Website", currentEntity.externalLinks || [])
            )
          : "",
      },
      {
        label: "Address",
        value: currentEntity.address,
        showCopy: true,
      },
    ];

    // Add assigned user fields if available
    if (currentEntity.assignedUser && currentEntity.assignedUser.length > 0) {
      const user = currentEntity.assignedUser[0];
      fields.push(
        {
          label: "Admin's Name",
          value: user.name,
          showCopy: true,
        },
        {
          label: "Admin's Email",
          value: user.email,
          showCopy: true,
        },
        {
          label: "Admin's Contact",
          value: user.contact,
          showCopy: true,
        },
        {
          label: "Admin's Role",
          value: user.role,
          showCopy: true,
        }
      );
    }

    return fields.filter((field) => field.value); // Only show fields with values
  };

  // Reusable InfoField component
  const InfoField = ({
    label,
    value,
    showCopy = true,
    isLink = false,
    linkHref = "",
    linkTarget = "_blank",
  }: {
    label: string;
    value: string | null | undefined;
    showCopy?: boolean;
    isLink?: boolean;
    linkHref?: string;
    linkTarget?: string;
  }) => {
    if (!value) return null;

    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={getValueFieldStyles(value)}>
            {isLink ? (
              <a
                href={linkHref}
                target={linkTarget}
                rel="noopener noreferrer"
                style={{ textTransform: "none", color: "#1B8368" }}
              >
                {value}
              </a>
            ) : (
              value
            )}
          </Typography>
          {showCopy && (
            <IconButton size="small" onClick={() => handleCopy(value)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "22px",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          borderBottom: "1px solid #ccc",
          color: "#3D5347",
          fontSize: "16px",
          fontWeight: "600",
        }}
      >
        {showLogs
          ? "Logs"
          : showReasonConfirmation
          ? "Reason"
          : `${
              entityType === "ORGANIZATION" ? "Organization" : "Individual"
            } Details`}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: isLoading ? "center" : "flex-start",
          overflowX: "hidden",
          minHeight: showLogs && activities.length > 0 ? "auto" : "400px",
          height: "100%",
        }}
      >
        {isLoading ? (
          <CircularProgress />
        ) : showReasonConfirmation ? (
          <ReasonConfirmation
            onConfirm={handleSubmit}
            newStatus={
              selectedAction === "unfreeze"
                ? "approved"
                : selectedAction === "approve"
                ? "approved"
                : "rejected"
            }
            reason={message}
            setReason={setMessage}
            reasonError={reasonError}
            setReasonError={setReasonError}
            isLoading={loading}
          />
        ) : showLogs ? (
          <ManagementLogs logs={activities} />
        ) : currentEntity ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              marginTop: "12px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              flex: "auto",
            }}
          >
            <Box>
              {/* ----- Account Status Section ----- */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <Typography variant="subtitle2">Account Status</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: currentEntity?.isFreezed
                        ? "#FF4949"
                        : currentEntity?.isVerified
                        ? "#00D23B"
                        : "#EDA920",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  >
                    {currentEntity?.isFreezed
                      ? "Frozen"
                      : currentEntity?.isVerified
                      ? "Approved"
                      : "Pending"}
                  </Typography>
                </div>
              </Box>

              {/* ----- Entity Fields ----- */}
              {getEntityFields().map((field, index) => (
                <InfoField
                  key={index}
                  label={field.label}
                  value={field.value}
                  showCopy={field.showCopy}
                  isLink={field.isLink}
                  linkHref={field.linkHref}
                />
              ))}

              {/* ----- Organization Documents ----- */}
              {entityType === "ORGANIZATION" &&
                isOrganization(currentEntity) &&
                (currentEntity?.organizationLinks?.length || 0) > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                      Organization Documents
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {currentEntity.organizationLinks?.map((link, idx) => (
                        <Link
                          key={idx}
                          href={link}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          📄 Document {idx + 1}
                        </Link>
                      ))}
                    </Box>
                  </>
                )}

              {/* ----- Created/Updated At ----- */}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ width: "50%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 2, textTransform: "capitalize" }}
                  >
                    Created At
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={getValueFieldStyles(currentEntity.createdAt)}
                    >
                      {currentEntity.createdAt
                        ? moment
                            .utc(currentEntity.createdAt)
                            .local()
                            .format("DD MMM, YYYY | hh:mm A")
                        : "Not Available"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: "50%" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 2, textTransform: "capitalize" }}
                  >
                    Last Updated At
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={getValueFieldStyles(currentEntity.updatedAt)}
                    >
                      {currentEntity.updatedAt
                        ? moment
                            .utc(currentEntity.updatedAt)
                            .local()
                            .format("DD MMM, YYYY | hh:mm A")
                        : "Not Available"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* ----- Action Buttons & Logs ----- */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "12px",
                mt: 3,
                gap: 2,
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  gap: "20px",
                }}
              >
                {getAllActions().map((action) => (
                  <Button
                    key={action.action}
                    variant="contained"
                    sx={{
                      width: "200px",
                      borderRadius: "20px",
                      bgcolor: action.color,
                      "&:hover": { bgcolor: action.color },
                      textTransform: "none",
                    }}
                    onClick={() => handleAction(action.action)}
                    disabled={loading}
                  >
                    {action.icon}
                    {action.label}
                  </Button>
                ))}
              </div>

              {/* Migrate Entity - All entity types (superadmin/admin only) */}
              {(loggedInUserInfo?.role === "superadmin" ||
                loggedInUserInfo?.role === "admin") && (
                <Button
                  onClick={() => setShowMigrationModal(true)}
                  variant="contained"
                  sx={{
                    width: "150px",
                    borderRadius: "12px",
                    bgcolor: "#1B8368",
                    "&:hover": { bgcolor: "#1B8368" },
                    textTransform: "none",
                  }}
                >
                  Migrate Entity
                </Button>
              )}

              <Button
                variant="text"
                onClick={() => setShowLogs(true)}
                sx={{
                  color: "#3D5347",
                  textDecoration: "underline",
                  textTransform: "none",
                  fontSize: "13.5px",
                  width: "150px",
                  fontWeight: "600",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Check Logs
              </Button>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteEntity}
        adminName={
          currentEntity
            ? entityType === "ORGANIZATION"
              ? (currentEntity as Organization).name
              : (currentEntity as Individual).name
            : ""
        }
        loading={deleteLoading}
      />

      {/* Migration Modal */}
      {showMigrationModal && currentEntity && (
        <MigrationModal
          open={showMigrationModal}
          onClose={() => setShowMigrationModal(false)}
          entityId={currentEntity._id}
          entityName={
            entityType === "ORGANIZATION"
              ? (currentEntity as Organization).name
              : (currentEntity as Individual).name
          }
          currentEntityType={
            entityType === "ORGANIZATION" ? "ORGANIZATION" : "INDIVIDUAL"
          }
          onMigrationComplete={(targetEntityType: string) => {
            onStatusUpdate();
            setShowMigrationModal(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default StatusHandlerModal;
